import * as THREE from 'three';
import './ui/hud.css';

import { arenaById } from './core/arenas/index.ts';
import { CATHEDRAL_TEST_ROSTER } from './core/arenas/testRoster.ts';
import { GameWorld, initPhysics } from './render/world.ts';
import { FirstPersonController } from './render/controller.ts';
import { Actors } from './render/actors.ts';
import { Viewmodel } from './render/viewmodel.ts';
import { Postprocess } from './render/postprocess.ts';
import {
  ANIMATE_DISTANCE, DRAW_DISTANCE, SHADOW_DISTANCE,
} from './render/mageLod.ts';
import { AssetRegistry } from './render/assets/registry.ts';
import { GameRoom } from './net/room.ts';
import { RoomTransport, loopback, type RoomLink } from './net/roomTransport.ts';
import { CLASSES, hpAt, ppAt } from './core/classes.ts';
import { defaultLoadout } from './core/spells.ts';
import { FACTIONS, TEAM_SIZE, TICK_DT } from './core/constants.ts';
import type { ClassId } from './core/constants.ts';
import { Hud } from './ui/hud.ts';
import { runSetup, type SetupChoice } from './ui/setup.ts';
import { runLobbyScreen } from './ui/lobbyScreen.ts';
import {
  countdown, headcount, maySwitch, type LobbyMember, type LobbySettings,
  BOT_CLASSES, BOT_NAMES,
} from './core/lobby.ts';
import type { ChatMessage } from './core/chat.ts';
import { runMainScreen } from './ui/mainScreen.ts';
import { connect, fetchLobbies, openLobby, toHttpUrl, toWsUrl } from './net/socketLink.ts';
import type { LobbySummary } from './core/lobby.ts';
import { LoadingScreen, uploadTextures, warmFrames } from './ui/loading.ts';
import { PerfOverlay } from './ui/perfOverlay.ts';
import { TIERS, startingQuality, storeTier, type Tier } from './render/quality.ts';
import { SoundStage } from './render/sound.ts';
import { makeChatLog, say } from './core/chat.ts';
import type { SimEvent, SimState } from './core/sim.ts';
import type { PlayerState } from './core/player.ts';

/*
 * Shared with the server, which seats the bots for a hosted lobby. See
 * `core/lobby.ts` — a server inventing its own names would give a player
 * different opponents depending on where the match happened to be running.
 */

/**
 * Holds the lobby open until the room is ready to start.
 *
 * The roster is assembled here rather than fetched, because there is not always
 * a server to fetch it from — you, and whatever bots were asked for. The screen
 * itself does not know the difference: it is handed a `LobbyView` and a set of
 * things a player can do, which is exactly what a socket will hand it later.
 *
 * Resolves when the countdown runs out or everybody is ready, which is the same
 * rule `countdown` applies in core.
 */
async function runLobby(
  root: HTMLElement,
  choice: SetupChoice,
  settings: LobbySettings | null,
  room: string | null,
  /**
   * The room this lobby *is*, when there is one.
   *
   * Null for a match with no server behind it, which still gets the local view
   * it always had — a roster of you and your bots, and a countdown this file
   * runs. With a link, every one of those comes from the room instead, which is
   * the point: one roster, one clock, one answer to who is ready.
   */
  link: RoomLink | null = null,
): Promise<void> {
  const said: ChatMessage[] = [];
  /*
   * The room, re-read on the same interval the screen redraws on.
   *
   * `collect` is how a link hands over everything since the last call, and
   * during a lobby that is exactly what is wanted — nothing else is listening
   * yet, because the arena does not exist.
   */
  let room$: SimState | null = null;
  const drain = (): void => {
    if (!link) return;
    const got = link.collect();
    room$ = got.state;
    for (const m of got.chat) said.push(m);
  };
  drain();
  let waited = 0;
  let ready = false;
  let done = false;
  /*
   * How long the room has been ready, which is what makes the last five seconds
   * count down rather than sit there. Reset whenever anybody stops being ready —
   * changing discipline, switching side — because the hurry is a promise that
   * everybody has finished choosing.
   */
  let readyFor = 0;

  const members = (): LobbyMember[] => {
    const you: LobbyMember = {
      id: 'you',
      name: choice.name,
      faction: choice.faction,
      cls: choice.cls,
      level: 1,
      ready,
      bot: false,
      joinedAt: 0,
    };

    // The bots the muster asked for, shown as what they are. A lobby of eleven
    // bots and one person is a different room from a full one.
    const bots: LobbyMember[] = [];
    for (const faction of FACTIONS) {
      for (let i = 0; i < choice.bots[faction]; i++) {
        bots.push({
          id: `bot_${faction}_${i}`,
          name: BOT_NAMES[(bots.length + i) % BOT_NAMES.length]!,
          faction,
          cls: BOT_CLASSES[i % BOT_CLASSES.length]!,
          level: 1,
          ready: true,
          bot: true,
          joinedAt: i + 1,
        });
      }
    }
    /*
     * A real room's roster replaces the invented one.
     *
     * Joining somebody else's match used to show a lobby containing nobody but
     * you and a countdown to a match that had already started — the screen was
     * built entirely from local guesses because there was nothing else to build
     * it from. There is now: the room summary carries who is in there, so a
     * joiner sees the actual sides before picking one.
     */
    /*
     * A real room's roster replaces the invented one, including you.
     *
     * `you` above is a guess this file maintains for the no-server case. With a
     * room there is no need to guess: the room knows your side, your discipline
     * and whether you have said you are ready, and so does everybody else's
     * screen. That is the difference between a lobby and three people each
     * imagining one.
     */
    const now = room$;
    if (now) {
      return now.roster.map((m, i) => ({
        id: m.id,
        name: m.name,
        faction: m.faction,
        cls: m.cls as ClassId,
        level: m.level,
        ready: m.ready,
        bot: m.id.startsWith('bot_'),
        joinedAt: i,
      }));
    }

    return [you, ...bots];
  };

  return new Promise<void>((resolve) => {
    const finish = (): void => {
      if (done) return;
      done = true;
      window.clearInterval(tick);
      root.hidden = true;
      root.innerHTML = '';
      resolve();
    };

    const screen = runLobbyScreen(root, {
      view: () => ({
        name: settings?.name ?? room ?? 'Practice',
        round: (room$?.series?.played ?? 0) + 1,
        bestOf: room$?.series?.bestOf ?? settings?.bestOf ?? 1,
        map: room$?.arena.name ?? 'Great Cathedral',
        nextMap: null,
        inProgress: false,
        /*
         * The room's clock when there is a room, and this file's only when there
         * is not.
         *
         * One clock is the whole point. Each client used to run its own, so
         * whoever pressed ready first watched five seconds tick away and dropped
         * into a match alone while everybody else still had two minutes on
         * screen.
         */
        secondsLeft: room$ ? room$.lobbyLeft : countdown(waited, members(), readyFor),
        wins: room$?.series?.wins ?? { order: 0, balance: 0, chaos: 0 },
        members: members(),
        youId: link?.playerId ?? 'you',
        /*
         * Whoever set the room's rules runs it.
         *
         * `room === null` used to be a fair test for that, because a host had no
         * room id — they created one at match load. They do now: a lobby is
         * opened on the server the moment its screen is, so the host holds an id
         * exactly like a joiner and quietly lost the host controls, bots
         * included. Having *settings* is what actually distinguishes them, since
         * those are the rules they chose and a joiner never has any.
         */
        isHost: settings !== null,
        maxPlayers: settings?.maxPlayers ?? 12,
      }),
      chat: () => said,
      loadout: () => choice.loadout,

      join: (faction) => {
        const heads = headcount(members());
        if (!maySwitch(heads, choice.faction, faction)) return;
        choice.faction = faction;
        ready = false;
        readyFor = 0;
        link?.reseat?.(faction, choice.cls, choice.loadout);
        link?.setReady?.(false);
        screen.refresh();
      },
      chooseClass: (cls) => {
        if (cls === choice.cls) return;
        choice.cls = cls;
        // A loadout belongs to a discipline; changing one discards the other.
        choice.loadout = defaultLoadout(cls);
        // And nobody is ready for a choice they have not confirmed.
        ready = false;
        readyFor = 0;
        link?.reseat?.(choice.faction, cls, choice.loadout);
        link?.setReady?.(false);
        screen.refresh();
      },
      setLoadout: (loadout) => {
        choice.loadout = [...loadout];
        ready = false;
        readyFor = 0;
      },
      setReady: (on) => {
        ready = on;
        readyFor = 0;
        // The room decides when everybody is ready, so it has to be told.
        link?.setReady?.(on);
        screen.refresh();
      },
      rename: (name) => {
        choice.name = name;
        // And to the room, which is what everybody else's screen is reading.
        link?.rename?.(name);
      },
      addBot: (faction) => {
        /*
         * A side holds eight mages, and you are one of them if you are on it.
         *
         * The same cap the muster used, for the same reason: a Chaos side with
         * no human on it could otherwise be one short of every other side, and
         * every roster in the game would be quietly a mage light.
         */
        /*
         * To the room when there is one.
         *
         * The room is joined before this screen opens, so a bot chosen here
         * cannot ride along on the join the way the ones chosen earlier do —
         * which is why the button sat there doing nothing. `choice.bots` is
         * still kept up for the no-server case, where this file is the room.
         */
        if (link) { link.addBot?.(faction); readyFor = 0; return; }

        const seats = TEAM_SIZE - (choice.faction === faction ? 1 : 0);
        if (choice.bots[faction] < seats) choice.bots[faction] += 1;
        readyFor = 0;
        screen.refresh();
      },
      say: (text) => {
        // Over the wire when there is one, so the room is the one place chat
        // exists and everybody sees the same conversation.
        if (link) { link.say('all', text); screen.refresh(); return; }
        said.push({
          id: said.length + 1,
          fromId: 'you',
          fromName: choice.name,
          faction: choice.faction,
          scope: 'all',
          text,
          at: waited,
        });
        screen.refresh();
      },
      leave: () => {
        // Back to the front door, which is a reload for the same reason leaving
        // a match is: there is one match per page, so the page is the match.
        location.reload();
      },
    });

    /*
     * Four times a second with a room behind it, once without.
     *
     * The local lobby only ever needed to move a clock. A room's lobby is
     * somebody else's roster arriving, somebody else's chat and somebody else's
     * ready tick, and a second of latency on those is the difference between a
     * shared room and three people watching a slideshow of one.
     */
    /*
     * Said once, because the difference between these two is the difference
     * between a shared lobby and everybody imagining their own, and it has not
     * been possible to tell them apart from the outside.
     */
    console.info(
      link
        ? `[lobby] room-backed (${link.playerId}), phase=${room$?.match?.phase ?? 'unknown'}`
        : '[lobby] local — no room behind this screen',
    );

    const tick = window.setInterval(() => {
      if (link) {
        drain();
        /*
         * The room says when it is done, and only the room.
         *
         * A snapshot that has not arrived yet, or one that arrives without a
         * match on it, means "still waiting" — not "go". Falling through to the
         * local countdown here would put this client into a match the room has
         * not started, which is exactly the split this whole change exists to
         * remove: the clock reads fifteen minutes and never moves, because the
         * room is still sitting in its lobby.
         */
        const phase = room$?.match?.phase;
        if (phase !== undefined && phase !== 'lobby') { finish(); return; }
        screen.refresh();
        return;
      }

      waited += 1;
      if (ready) readyFor += 1;
      if (countdown(waited, members(), readyFor) <= 0) finish();
      else screen.refresh();
    }, link ? 250 : 1000);
  });
}

/** What this build calls itself. Baked in by Vite; see `vite.config.ts`. */
declare const __BUILD__: string;

async function main(): Promise<void> {
  /*
   * Announced before anything else happens.
   *
   * The first question when a fix appears not to have worked is whether the fix
   * is actually running, and inferring that from which bugs are present is slow
   * and often wrong. One line in the console answers it.
   */
  // eslint-disable-next-line no-console
  console.log(`Mage Wars ${typeof __BUILD__ === 'string' ? __BUILD__ : 'dev'}`);

  await initPhysics();

  const canvas = document.querySelector<HTMLCanvasElement>('#viewport');
  const prompt = document.querySelector<HTMLElement>('#prompt');
  const setupRoot = document.querySelector<HTMLElement>('#setup');
  const loadingRoot = document.querySelector<HTMLElement>('#loading');
  const mainRoot = document.querySelector<HTMLElement>('#main');
  const lobbyRoot = document.querySelector<HTMLElement>('#lobby');
  const params = new URLSearchParams(location.search);
  if (!canvas || !prompt || !setupRoot || !loadingRoot || !mainRoot) {
    throw new Error('index.html is missing #viewport, #prompt, #setup, #loading or #main');
  }

  /*
   * Where the rooms are, if they are anywhere.
   *
   * Set at build time. With it, the browser lists real lobbies and joining one
   * opens a socket; without it, everything runs in this tab exactly as it always
   * has — which is the arrangement worth keeping, because tweaking the game
   * between test sessions should not need a server running beside it.
   */
  const configured = (import.meta as unknown as { env?: Record<string, string> })
    .env?.VITE_SERVER_URL;
  const joinKey = (import.meta as unknown as { env?: Record<string, string> })
    .env?.VITE_JOIN_SECRET ?? params.get('key') ?? undefined;

  const serverUrl = configured?.replace(/\/$/, '')
    ?? params.get('server')
    ?? '';

  /*
   * Said out loud at startup, because the alternative is guessing.
   *
   * Every "could not reach the server" so far has come down to which address the
   * build was given, and a build's environment variables are baked in at compile
   * time and invisible from the running page. Printing the resolved address —
   * and the reason it is unusable, when it is — turns a support conversation
   * into a glance at the console.
   */
  if (serverUrl) {
    const ws = toWsUrl(serverUrl);
    if ('problem' in ws) console.error(`[server] ${ws.problem}`);
    else console.info(`[server] ${toHttpUrl(serverUrl)} (sockets at ${ws.url})`);
  } else {
    console.info('[server] none configured — matches run locally in this tab');
  }

  // Either a room to join on a server, or settings for one to run here.
  let lobbySettings: LobbySettings | null = null;
  let joining: string | null = null;

  if (params.get('skiplobby') !== '1') {
    const chosen = await new Promise<
      { kind: 'create'; settings: LobbySettings } | { kind: 'join'; room: string }
    >((resolve) => {
      let close = (): void => { mainRoot.hidden = true; };
      /*
       * Resolved here rather than at the renderer, because the screen has to
       * show it and the screen comes first.
       *
       * That ordering is the whole reason the control can live on this screen at
       * all: a tier decides the torch light count, which is compiled into every
       * lit material, and the shadow map size, which is an allocated texture.
       * Neither can change once the arena exists. Chosen before any of it is
       * built, a tier needs no reload and no warning — it is simply the setting
       * the world is then made with.
       */
      const picked0 = startingQuality(params.get('quality'));
      runMainScreen(mainRoot, {
        playerName: 'Mage',
        graphics: {
          tier: picked0.tier,
          reason: picked0.reason,
          /*
           * Stored, and nothing else — `startingQuality` reads it back below.
           *
           * Keeping a variable alongside the store would be two sources for one
           * answer, and the store is the one that has to be right anyway since
           * it is what a later session reads.
           */
          choose: storeTier,
        },
        list: async () => (serverUrl
          ? (await fetchLobbies(serverUrl)) as LobbySummary[]
          : []),
        join: (room) => { close(); resolve({ kind: 'join', room }); },
        // Closed through the screen's own handle, which also stops it polling
        // for lobbies through a match nobody wants interrupted.
        create: (settings) => { close(); resolve({ kind: 'create', settings }); },
        dismiss: (fn) => { close = fn; },
      });
    });

    if (chosen.kind === 'join') {
      joining = chosen.room;
    } else {
      lobbySettings = chosen.settings;
      /*
       * Opened on the server now, not when the match loads.
       *
       * The lobby is a screen the host sits in for up to two minutes, and the
       * room used to be created only once they had finished with it and the
       * arena had loaded — so a friend refreshing the join list saw nothing at
       * all until the match had already started, which is precisely too late to
       * join it. Opening here means the lobby is listed from the moment it
       * exists, which is what a lobby is for.
       *
       * Joined below like any other room. A failure falls back to a local match,
       * the same as before, and says so rather than pretending.
       */
      if (serverUrl) {
        try {
          joining = await openLobby(serverUrl, {
            name: chosen.settings.name,
            mapId: chosen.settings.mapPool[0],
            mapPool: chosen.settings.mapPool,
            maxPlayers: chosen.settings.maxPlayers,
            xpScale: chosen.settings.xpScale,
            roundSeconds: Math.round(chosen.settings.timeLimit * 60),
            bestOf: chosen.settings.bestOf,
            carryXp: chosen.settings.carryXp,
            secret: joinKey,
          });
        } catch (err) {
          const why = err instanceof Error ? err.message : String(err);
          console.warn(`[lobby] could not open a room on the server: ${why}`);
          alert(
            `Could not open a lobby on the server:\n\n${why}\n\n`
            + 'Starting a local match instead — nobody else will be able to join it.',
          );
        }
      }
    }
  }

  // Nothing exists until the muster is done: the transport cannot be built
  // without knowing who is playing and on which side.
  /*
   * The muster, only when there is no lobby to replace it.
   *
   * Being asked for a faction and a discipline twice — once on a screen before
   * the room and again in the room itself — is asking the same question twice
   * and answering it in two places, and the second answer silently won. So a
   * lobby means straight to the lobby.
   */
  const useLobby = Boolean(lobbyRoot) && params.get('skiplobby') !== '1';
  const choice: SetupChoice = useLobby
    ? {
      name: 'Mage',
      faction: 'order',
      cls: 'magician',
      loadout: defaultLoadout('magician'),
      bots: { order: 0, balance: 0, chaos: 0 },
      difficulty: 'normal',
    }
    : await runSetup(setupRoot);

  /*
   * The lobby, between choosing and playing.
   *
   * Built against a view this file maintains rather than one a server sends,
   * because there is not always a server: the roster is you and whatever bots
   * were asked for, the chat is local, and the countdown is the same two minutes
   * a room would give. When a socket fills this in instead, the screen does not
   * change — it is handed a `LobbyView` either way, which is what it was built
   * for.
   */
  /*
   * The socket opens *before* the lobby, not after it.
   *
   * This is the change the whole pass is about. The lobby used to be a screen
   * each client drew for itself — its own roster of one, its own countdown, its
   * own idea of who was ready — because the room was not joined until the screen
   * closed. Two people sitting in the same lobby could not see each other, and
   * either of them hitting ready started a match for themselves alone.
   *
   * Joined first, the lobby is the room: one roster, one chat, one clock, and
   * `ready` is a fact the room holds about each of its members rather than a
   * local boolean.
   */
  const hosting = Boolean(serverUrl) && !joining && lobbySettings !== null;
  const link = (joining || hosting)
    ? connect(
      serverUrl,
      joining ?? {
        create: {
          name: lobbySettings!.name,
          mapId: lobbySettings!.mapPool[0],
          mapPool: lobbySettings!.mapPool,
          maxPlayers: lobbySettings!.maxPlayers,
          xpScale: lobbySettings!.xpScale,
          roundSeconds: Math.round(lobbySettings!.timeLimit * 60),
          bestOf: lobbySettings!.bestOf,
          carryXp: lobbySettings!.carryXp,
        },
      },
      {
        name: choice.name,
        faction: choice.faction,
        cls: choice.cls,
        loadout: choice.loadout,
        secret: joinKey,
        bots: { ...choice.bots },
      },
    )
    : null;

  let remote = null;
  if (link) {
    try {
      remote = await link.ready;
    } catch (err) {
      const why = err instanceof Error ? err.message : String(err);
      link.close();
      if (!hosting) throw new Error(`Could not join that room: ${why}`);
      console.warn(`[lobby] could not open a room on the server: ${why}`);
      alert(
        `Could not open a lobby on the server:\n\n${why}\n\n`
        + 'Starting a local match instead — nobody else will be able to join it.',
      );
    }
  }

  /*
   * The lobby, now that there is a room to hold it.
   *
   * Skipped entirely for somebody arriving into a match already being fought:
   * they asked to play, the round is running, and a waiting room for a wait that
   * is already over is a screen between them and the game. The loading screen
   * says what is happening instead.
   */
  const inProgress = remote !== null && remote.collect().state.match.phase !== 'lobby';
  if (useLobby && lobbyRoot && !inProgress) {
    await runLobby(lobbyRoot, choice, lobbySettings, joining, remote);
  } else if (inProgress) {
    console.info('[lobby] match already running — going straight in');
  }

  const loading = new LoadingScreen(loadingRoot);

  // Art is loaded before the world is built, so a role that has a model behind
  // it is used from the first frame. Anything missing or broken falls back to
  // the gray-box, which is what the whole game rendered as until now.
  const assets = await AssetRegistry.load();
  const missing = assets.report().filter((r) => r.problem);
  if (missing.length) {
    console.warn(`[assets] ${missing.length} role(s) fell back to gray-box geometry`);
  }

  /*
   * Which arena to play. `?arena=cathedral` selects the old gray-box, which is
   * useful for telling a level problem apart from an engine one — an unknown id
   * quietly falls back rather than dropping the player into a black screen.
   */
  /*
   * The lobby's map, if one was chosen. Its first entry for now — the rest of a
   * pool is a rotation, and rotating needs a room that outlives a match.
   */
  /*
   * Joining a server, if the browser sent us to one.
   *
   * Done before the level is built, because the room decides which map is being
   * played and drawing a different one from the one collisions are resolved
   * against is the worst kind of desync — everything looks right and nothing
   * lines up.
   */
  /*
   * One socket, whether the room already exists or is about to.
   *
   * A host's "create" used to run the match in this tab and tell the server
   * nothing, which is why a lobby never appeared on anybody else's list — there
   * was nothing to appear. It now opens a room on the server and joins it, over
   * the same connection, and only falls back to a local match when there is no
   * server configured at all.
   */

  const picked = arenaById(
    (remote ? link?.mapId() : null) ?? lobbySettings?.mapPool[0] ?? params.get('arena'),
  );
  /*
   * Decided here, before anything is built, because the parts of it that matter
   * most cannot be changed later.
   *
   * The torch light count is compiled into every lit material and the shadow map
   * is an allocated texture, so both are settled at construction. Pixel ratio,
   * bloom and the shadow cadence can all be turned live and are.
   */
  /*
   * Read after the main screen, which is what lets the dropdown there work with
   * no reload: a choice made on that screen is already in the store by the time
   * this runs, and the world has not been built yet. `?quality` still wins, on
   * purpose — it is a tool for comparing two runs rather than a setting.
   */
  const quality = startingQuality(params.get('quality'));
  console.info(
    `[mage-wars] quality: ${quality.tier} (${quality.reason}). `
    + 'mw.perf.tier("low"|"medium"|"high") to change.',
  );

  const world = new GameWorld(picked.geometry, assets, quality);
  // The server's arena when there is one — it carries who holds which pool, and
  // a freshly built copy would say nobody does.
  const arena = link?.arena() ?? picked.make();

  let pendingEvents: SimEvent[] = [];

  /*
   * The room, and one client attached to it.
   *
   * This used to be a single object that owned the simulation *and* pretended to
   * have come off a wire, which is why it could never hold a second player: the
   * thing holding the world also assumed it was the one being played. Locally
   * the two halves sit in the same process and the link between them is a
   * function call; over a socket the room is elsewhere and nothing on this side
   * changes but the pipe.
   */
  const room = new GameRoom('local', {
    mode: 'shrines',
    arena,
    // Bots path over the same geometry the colliders come from, so they cannot
    // walk anywhere a player could not.
    geometry: picked.geometry,
    difficulty: choice.difficulty,
    xpScale: lobbySettings?.xpScale,
    capacity: lobbySettings?.maxPlayers,
  }, world);

  const localId = 'player';
  /*
   * A seat, either way.
   *
   * Playing locally this *is* the player. Against a server it is a stand-in that
   * exists only to seed the renderer with something to draw until the first
   * snapshot arrives — a fraction of a second later, and replaced entirely by
   * what the room sends.
   */
  room.join(localId, choice.name, choice.faction, choice.cls, choice.loadout);

  /*
   * Bots belong to whoever owns the simulation.
   *
   * On a server that is the server, and filling a local room with them would
   * mean simulating a dozen mages nobody will ever see — the snapshots arriving
   * over the wire are the only thing this tab draws.
   */
  const botTotal = remote ? 0 : FACTIONS.reduce((n, f) => n + choice.bots[f], 0);
  if (remote) {
    // Nothing to populate: the room is elsewhere.
  } else if (botTotal > 0) {
    for (const faction of FACTIONS) {
      for (let i = 0; i < choice.bots[faction]; i++) {
        room.addBot(BOT_NAMES[i % BOT_NAMES.length], faction, BOT_CLASSES[i % BOT_CLASSES.length]);
      }
    }
  } else {
    // Nobody asked for a roster, so this is a solo session: fall back to the
    // test combatants, which exist to give one player something to shoot and
    // something that shoots back.
    for (const d of CATHEDRAL_TEST_ROSTER) {
      room.addDummy(d.name, d.faction, d.cls, d.pos, { attacks: d.attacks, level: d.level });
    }
  }

  /*
   * The same transport either way.
   *
   * All that differs is the pipe: a function call to a room in this tab, or a
   * socket to one somewhere else. Nothing below this line knows which, which is
   * the whole return on having split the room from the transport.
   */
  const transport = new RoomTransport(
    remote ?? loopback(room, localId, true),
    room.authoritative,
    { onEvents: (events: SimEvent[]) => pendingEvents.push(...events) },
  );

  /*
   * The state to *build* from. The frame reads its own; see below.
   *
   * These two are the seed the transport was constructed with, and they are
   * exactly right for standing the renderer up — the controller needs somewhere
   * to put the camera and the actors need a roster to mirror. They are not right
   * for drawing, and treating them as though they were is what this whole
   * session's stuck match turned out to be.
   */
  const seed = transport.getState();
  const seedLocal = seed.players.find((p) => p.id === transport.localPlayerId)
    ?? seed.players.find((p) => p.id === localId)!;

  const controller = new FirstPersonController();
  /** A server owns the room; this client only predicts its own mage. */
  const hosted = remote !== null;
  controller.attach(canvas);
  const actors = new Actors(world.scene, seed, assets, controller.camera);
  const viewmodel = new Viewmodel(assets);
  viewmodel.applyAspect(window.innerWidth / window.innerHeight);
  const hud = new Hud(transport.localPlayerId);

  /*
   * Every mage has a capsule in this world, from the same class the server
   * uses. In a local match the room keeps them in line each tick; in a hosted
   * one the frame loop does, from the latest snapshot. Seeded now so the very
   * first predicted tick already has a body to move.
   */
  world.syncBodies([seedLocal]);

  /*
   * Against a server, run our own mage ahead of it.
   *
   * Given the same world the camera follows, so the capsule a prediction is
   * resolved against is the very one being drawn. Playing locally there is
   * nothing to be ahead of — the room answers in the same call — so this stays
   * off and the code path is the one that has always run.
   */
  if (remote) transport.predictWith(world);

  // The pause card, built once from the class actually being played.
  const localMage = transport.getState().players
    .find((p) => p.id === transport.localPlayerId);
  if (localMage) hud.primer(localMage);

  /*
   * No MSAA, because there is nothing here for it to sample.
   *
   * `antialias: true` multisamples the *default framebuffer*. The world is not
   * drawn there — it goes through the composer into a render target, and what
   * finally reaches the canvas is a fullscreen quad carrying the composited
   * result as a texture. A quad has two triangles and no interior edges, so
   * every extra sample resolves to the same colour. The flag bought a
   * multisampled backbuffer worth of bandwidth and memory and returned exactly
   * no antialiasing.
   *
   * If it is wanted back it has to happen inside the composer — an `SMAAPass`,
   * or `samples` on the composer's render target — not out here.
   */
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = picked.geometry.lighting?.exposure ?? 1.05;
  // One shadow-casting light, softened. PCFSoft costs a little more than PCF and
  // is the difference between a shadow that reads as depth and one that reads as
  // a stencil.
  renderer.shadowMap.enabled = quality.shadows;
  renderer.shadowMap.type = quality.shadowSoft
    ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
  // Two passes a frame, so the clearing is done by hand. Left on, the second
  // render would wipe the world and leave a wand floating in an empty void.
  renderer.autoClear = false;

  // Built before the first resize, which calls into it. Half-resolution bloom
  // over the world only; the wand is drawn after and stays out of it.
  const post = new Postprocess(
    renderer, world.scene, controller.camera, { enabled: quality.bloom },
  );

  const resize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Pixel ratio first: setSize derives the backing buffer from it.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.pixelRatio));
    // updateStyle left at its default so the canvas keeps a CSS size equal to
    // the window, whatever the backing buffer resolution is.
    renderer.setSize(w, h);
    controller.applyAspect(w / h);
    viewmodel.applyAspect(w / h);
    post.resize(w, h);

    // Cheap self-check: if the drawing surface and the window ever disagree, the
    // crosshair stops marking the centre of the projection and aim drifts.
    const canvasWidth = Math.round(canvas.getBoundingClientRect().width);
    if (Math.abs(canvasWidth - w) > 1) {
      console.error(
        `Canvas is ${canvasWidth}px wide but the window is ${w}px. ` +
        'Aim will not line up with the crosshair.',
      );
    }
  };

  /*
   * Sound waits for a click, because browsers refuse to start an `AudioContext`
   * without one — and a context created outside a gesture is not merely silent,
   * it is suspended, and everything played into it queues up and arrives at once
   * when it finally resumes. The same click that grabs the pointer does it.
   */
  const sound = new SoundStage();
  document.addEventListener('click', () => { void sound.start(); }, { once: true });

  /*
   * Leaving a match.
   *
   * A reload rather than a teardown, and deliberately so: the alternative is
   * unwinding a renderer, a physics world, an audio context and a running
   * simulation in the right order, and getting one of them wrong leaves a
   * half-disposed game that looks fine until it does not. There is exactly one
   * match per page today, so the page *is* the match, and a reload is the only
   * teardown that cannot be incomplete. It becomes real work the day a client
   * moves between rooms without reloading, and not before.
   */
  document.querySelector<HTMLButtonElement>('#leave-match')?.addEventListener('click', () => {
    document.exitPointerLock();
    location.reload();
  });

  window.addEventListener('resize', resize);

  // Dragging the window to a monitor with different scaling, or zooming the
  // browser, changes devicePixelRatio without firing a resize event.
  let ratioWatch: MediaQueryList | null = null;
  const watchPixelRatio = (): void => {
    ratioWatch = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    ratioWatch.addEventListener('change', () => {
      resize();
      watchPixelRatio();
    }, { once: true });
  };
  watchPixelRatio();

  resize();

  document.addEventListener('pointerlockchange', () => {
    prompt.hidden = document.pointerLockElement === canvas;
  });

  /**
   * The local mage as the transport has them *now*.
   *
   * A function rather than a captured reference, because on a hosted lobby every
   * snapshot is a new object — the console holding the seed player would edit
   * something nothing draws, which is a confusing way to discover that the thing
   * you are poking is not the thing on screen.
   */
  const here = (): PlayerState => transport.getState().players
    .find((p) => p.id === transport.localPlayerId) ?? lastLocal;

  // Dev hook: `mw` in the browser console reaches the live simulation.
  Object.assign(window, {
    mw: {
      transport, world, controller, assets, post,
      /*
       * Live, not captured. The console hook used to hold the seed state and the
       * seed player, which on a hosted lobby are a photograph of the moment
       * before the match — the same staleness that froze the renderer.
       */
      get state() { return transport.getState(); },
      get local() { return here(); },
      /** Swap class in place — the fastest way to feel how differently ley behaves. */
      setClass(cls: ClassId): string {
        here().cls = cls;
        here().hpMax = hpAt(cls, here().level);
        here().ppMax = ppAt(cls, here().level);
        here().hp = here().hpMax;
        here().pp = here().ppMax;
        here().loadout = defaultLoadout(cls);
        here().cooldowns = here().loadout.map(() => 0);
        here().selectedSlot = 0;
        return `now a ${CLASSES[cls].name}: ${CLASSES[cls].blurb}`;
      },
      /** Live lighting tuning, so the mood can be dialled in without a reload. */
      setTorches(intensity: number, range?: number): void {
        for (const t of world.torches) {
          t.intensity = intensity;
          if (range !== undefined) t.distance = range;
        }
      },
      setExposure(v: number): void { renderer.toneMappingExposure = v; },
      /**
       * The knobs behind the grade, live.
       *
       * These are the numbers most worth playtesting and least worth rebuilding
       * to try, so they are reachable from the console: the balance between a
       * cool key that says what colour a thing is and warm torches that say
       * where you are, is exactly the thing a screenshot cannot settle.
       */
      setMoon(intensity: number, colour?: number): void {
        world.moon.intensity = intensity;
        if (colour !== undefined) world.moon.color.setHex(colour);
      },
      setAmbient(intensity: number, colour?: number): void {
        world.ambient.intensity = intensity;
        if (colour !== undefined) world.ambient.color.setHex(colour);
      },
      setFog(density: number, colour?: number): void {
        const fog = world.scene.fog as THREE.FogExp2 | null;
        if (!fog) return;
        fog.density = density;
        if (colour !== undefined) fog.color.setHex(colour);
      },
      setTorchColor(colour: number): void {
        for (const t of world.torches) t.color.setHex(colour);
      },
      /** Draw distances for mages. See render/mageLod.ts for why they exist. */
      lod(): { shadow: number; animate: number; draw: number } {
        return {
          shadow: SHADOW_DISTANCE, animate: ANIMATE_DISTANCE, draw: DRAW_DISTANCE,
        };
      },
      /** Horizontal field of view in degrees. 90–106 is the usual competitive range. */
      setFov(deg: number): void {
        controller.horizontalFov = deg;
        controller.applyAspect(window.innerWidth / window.innerHeight);
        viewmodel.applyAspect(window.innerWidth / window.innerHeight);
      },
    },
  });

  /*
   * `?relevancy=1` runs the network's visibility rule against the local match.
   *
   * There is nothing to protect against your own bots — the point is to find out
   * what the rule *feels* like before there is a server to find it out on:
   * whether enemies pop at the edge of a pillar, whether a Fire Ball lobbed over
   * a wall arrives unannounced, whether hidden Signs make the Arcanist dangerous
   * or merely irritating.
   */
  if (params.get('relevancy') === '1') transport.filterView(true);

  /*
   * Chat: Enter for the team, Shift+Enter for everybody.
   *
   * Locally there is only one voice, so a line is spoken and heard in the same
   * breath. Over a network the send goes to the server and the `hear` arrives
   * back as a message like anyone else's — which is why they are already two
   * calls rather than one, and why the rules live in core where both ends can
   * apply them.
   */
  const chatLog = makeChatLog();
  controller.suspendWhile(() => hud.typing);

  hud.onChat((scope, text) => {
    const me = transport.getState().players.find((p) => p.id === transport.localPlayerId);
    if (!me) return;
    const state = transport.getState();
    const said = say(chatLog, me, scope, text, state.match.phase, state.tick * TICK_DT);
    if (said) hud.hear(said, me);
  });

  window.addEventListener('keydown', (e) => {
    if (e.code !== 'Enter' && e.code !== 'NumpadEnter') return;
    if (hud.typing) return;
    e.preventDefault();
    hud.openChat(e.shiftKey ? 'all' : 'team', transport.getState().match.phase);
  });

  transport.start();

  const clock = new THREE.Clock();
  let respawnGuard = false;

  /*
   * The handful of knobs that decide how much work a frame is.
   *
   * Not a settings screen — that is a larger job and wants a menu, a store and a
   * decision about defaults per machine. This is the smaller thing underneath
   * it: the numbers gathered in one place with names, reachable from `mw.perf`
   * in the console, so that "is it the shadows?" can be answered in ten seconds
   * on the machine that is actually slow rather than inferred from a profile
   * taken on the machine that is fast.
   */
  let shadowTick = 0;

  /*
   * `?perf=1` to start with it up, F3 to toggle.
   *
   * F3 rather than a HUD control because it should be reachable on somebody
   * else's machine over a voice call without explaining where a menu is, and
   * because it must not be something a player can find by accident.
   */
  const stats = new PerfOverlay(params.get('perf') === '1');
  stats.label = quality.tier;
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'F3') return;
    e.preventDefault();
    stats.toggle();
  });

  /*
   * Reachable from the console, alongside the rest of the dev hooks.
   *
   * Assigned separately rather than folded into the `mw` object above because
   * that one is built before the renderer's frame state exists. Both land on the
   * same `mw`.
   */
  Object.assign((window as unknown as { mw: Record<string, unknown> }).mw, {
    perf: {
      /** Show or hide the overlay. Same as F3. */
      toggle: (): void => stats.toggle(),
      /** Frames between shadow-map redraws. 1 every frame, 0 turns them off. */
      shadows(every: number): void {
        quality.shadowInterval = Math.max(1, Math.round(every));
        if (every === 0) {
          renderer.shadowMap.enabled = false;
          renderer.shadowMap.autoUpdate = false;
        } else {
          renderer.shadowMap.enabled = true;
        }
      },
      /**
       * Force a pixel ratio, for finding out how much of a slow frame is fill.
       *
       * The fastest single test there is: if halving this fixes it, the answer
       * is pixels and nothing on the CPU side is worth looking at yet.
       */
      pixelRatio(ratio: number): void {
        renderer.setPixelRatio(ratio);
        renderer.setSize(window.innerWidth, window.innerHeight);
        post.resize(window.innerWidth, window.innerHeight);
      },
      /**
       * Switch quality tier. Remembered, and reloads.
       *
       * A reload rather than a live change because the two most valuable knobs
       * cannot be turned in place: the torch light count is part of every lit
       * material's shader program, and changing it recompiles the whole arena
       * mid-match, which is the stall the loading screen exists to hide. So the
       * choice is stored and the page is restarted through the door that already
       * handles restarting cleanly.
       */
      tier(name: Tier): void {
        if (!(name in TIERS)) {
          console.warn(`unknown tier "${name}"; expected low, medium or high`);
          return;
        }
        storeTier(name);
        location.reload();
      },
      /** What the current tier actually resolved to, and why. */
      settings: (): unknown => ({ ...quality }),
      /** Bloom off, which is the other half of the fill-rate question. */
      bloom(on: boolean): void { post.setEnabled(on); },
      /**
       * Half-width of the shadow frustum, metres.
       *
       * Turn it down until static shadows visibly wink out at the edge of
       * vision, then back off. The arena is 77 by 72, so anything past about 38
       * covers all of it wherever you stand and culls nothing.
       */
      shadowExtent(metres: number): void { world.setShadowExtent(metres); },
      /** What the GPU actually is, past the browser's generic string. */
      gpu(): string {
        const gl = renderer.getContext();
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (!dbg) return 'WEBGL_debug_renderer_info unavailable';
        return String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL));
      },
    },
  });

  /** The map on screen, so a repeat of the same one is not a rebuild. */
  let currentMapId = picked.id;

  /**
   * Puts the next round's map on screen.
   *
   * A swap rather than a reload: the scene, the composer, the actors, the
   * viewmodel and every shader warmed at load all survive, because what changes
   * is the level's contents and not the world holding them. That is the whole
   * reason it can happen inside a ten-second freeze instead of behind a loading
   * screen.
   *
   * Actors are cleared because their spell effects belong to the round that has
   * ended — a bolt still travelling across a map that no longer exists.
   */
  const swapMap = (mapId: string): void => {
    const next = arenaById(mapId);
    world.rebuildLevel(next.geometry);
    actors.clearTransient();
    // The shadow camera follows the player and is snapped to a texel grid; after
    // a teleport onto a new spawn it should start from there rather than sliding
    // across the map over the next few frames.
    world.followShadow(controller.camera.position);
    console.info(`[mage-wars] round map: ${next.name}`);
  };

  /**
   * How many frames in a row may throw before the loop gives up.
   *
   * One bad frame is worth surviving — a null that resolves on the next tick, a
   * texture not finished uploading. Sixty in a row is not a glitch, it is a
   * broken build, and spinning on it at sixty hertz filling the console helps
   * nobody.
   */
  const FRAME_FAILURE_LIMIT = 60;
  let consecutiveFailures = 0;

  /**
   * Runs a frame, and survives one that throws.
   *
   * The loop re-schedules itself at the end of its own body, so *any* exception
   * anywhere in a frame stops the game dead: no next frame, no simulation
   * advancing, no error on screen. The tab just quietly stops being a game while
   * the console holds the reason — which is why a stuck warm-up, a HUD that
   * looks half-built and a match that never starts are all the same bug wearing
   * three faces.
   *
   * It used to be survivable by accident. There were two `requestAnimationFrame`
   * chains running — a duplicate kick that doubled the cost of every frame — and
   * when one died the other carried on, so a throwing frame showed up as the
   * game being mysteriously slow rather than as the game stopping. Removing the
   * duplicate was right and made this failure mode reachable, so it is now
   * handled on purpose rather than by redundancy nobody meant to have.
   *
   * The error is put on screen because the console is not where somebody
   * playtesting on a Surface is looking.
   */
  const safeFrame = (): void => {
    try {
      frame();
      consecutiveFailures = 0;
    } catch (err) {
      consecutiveFailures += 1;
      console.error(`[frame] ${String(err)}`, err);
      if (consecutiveFailures === 1) showFrameError(err);
      if (consecutiveFailures < FRAME_FAILURE_LIMIT) requestAnimationFrame(safeFrame);
      else console.error('[frame] giving up after 60 consecutive failures');
    }
  };

  /** The last state that had us in it, so one thin snapshot cannot blank the HUD. */
  let lastState = seed;
  let lastLocal = seedLocal;

  const frame = (): void => {
    const frameStart = performance.now();
    const dt = Math.min(0.05, clock.getDelta());
    const elapsed = clock.elapsedTime;

    const input = controller.readInput();

    /*
     * Input, then simulate, then follow.
     *
     * The order used to be the other way round: the controller moved the body
     * and wrote the answer into the simulation, which is what made the one mage
     * the player controls the one mage the simulation was not in charge of.
     * Now core moves everybody — asking the controller to resolve a capsule when
     * it gets to the local player — and the camera reads the result.
     */
    stats.begin('sim');
    transport.sendInput(input);
    transport.advance(dt);
    stats.begin('physics');
    world.step();
    stats.end();

    /*
     * Read fresh, every frame. This was the bug.
     *
     * `state` used to be fetched once, before the loop, and everything below
     * drew from that one object. It worked — for a *local* match, where the
     * transport hands back the room's own `SimState` and the room mutates it in
     * place, so a reference taken at startup stays live for ever.
     *
     * A socket does not do that. Each snapshot is decoded into a **new** object
     * and `latest` is reassigned to it, so the captured reference stayed pointing
     * at the seed state the transport was constructed with: the local room, with
     * the player seated, at tick zero, phase `warmup`, ten seconds on the clock.
     * Nothing advanced it because nothing was meant to — on a hosted lobby the
     * server advances and this client only collects.
     *
     * So the match sat frozen at `warm=10.0` for ever while the render loop ran
     * at three hundred frames a second drawing a state that had never moved. It
     * looked like a stuck warm-up because the warm-up is the first thing that
     * would have changed; it was really every frame drawing a photograph. The
     * ten-second freeze did not cause it, it revealed it — before, the first
     * tick flipped the phase to `live` and a frozen state was merely a match
     * where nothing ever happened.
     */
    const state = transport.getState();
    const local = state.players.find((p) => p.id === transport.localPlayerId)
      ?? state.players.find((p) => p.id === localId);
    if (local) {
      lastState = state;
      lastLocal = local;
    }
    // A snapshot that arrives without us in it — the moment before a respawn is
    // acknowledged, or a relevancy filter that has not caught up — is not a
    // reason to draw an empty world. The last good one stands until the next.
    const shown = local ? state : lastState;
    const me = local ?? lastLocal;
    
    // Drawn part-way into the tick that has not happened yet, so movement is
    // smooth at whatever rate the monitor asks for rather than stepping at
    // sixty hertz.
    controller.follow(me, transport.localAlpha, dt);

    // After the simulation, not before: a wall cast this tick has to have its
    // collider in place before the next frame's controller.update walks into it.
    world.syncBarriers(shown.barriers);
    /*
     * Everybody else's capsule where the latest snapshot has them, so your
     * predicted movement bumps into the same mages the server's does. In a local
     * match the room has already done this every tick, and doing it again is a
     * handful of writes. Your own body is moved by prediction and re-placed from
     * the simulation at the start of every move, so placing it here is harmless.
     */
    if (hosted) world.syncBodies(shown.players);
    // The eye decides which fixtures hold the pooled lights.
    world.tickLights(elapsed, here().pos, dt);
    // ...and where the shadow camera is pointed. Cheap, and it has to happen
    // whether or not this frame redraws the map, so that the frame that *does*
    // redraw it finds the camera already in the right place.
    world.followShadow(controller.camera.position);

    // The simulation may have teleported us — to Valhalla, or back from it.
    if (me.inValhalla !== respawnGuard) {
      respawnGuard = me.inValhalla;
      world.place(me);
      controller.teleport(me);
    }

    if (pendingEvents.length) {
      for (const ev of pendingEvents) {
        /*
         * A round starting somewhere else.
         *
         * The room rotates the map between rounds and says so; without this the
         * client keeps drawing the map everybody has just left while collisions
         * and line of sight resolve against the new one. Which is the worst kind
         * of desync — everything looks right and nothing lines up, so a player
         * reports walking through a wall rather than the map being wrong.
         *
         * Only when it has actually changed. A single-map pool sends this event
         * every round and rebuilding for it would be a stall on purpose.
         */
        if (ev.type === 'round-start' && ev.mapId !== currentMapId) {
          currentMapId = ev.mapId;
          swapMap(ev.mapId);
          continue;
        }

        // The kick follows a cast that actually happened, not a key press.
        if (ev.type === 'cast' && ev.playerId === transport.localPlayerId) viewmodel.punch();

        /*
         * A bolt carries its own endpoints, because a hitscan leaves nothing for
         * the renderer to follow — the beam is the whole spell and it exists for
         * a quarter of a second.
         */
        if (ev.type === 'beam') {
          actors.addBolt(
            ev.from, ev.to, ev.element, ev.spellId,
            ev.playerId === transport.localPlayerId,
          );
          continue;
        }

        if (ev.type !== 'arc') continue;
        const from = shown.players.find((p) => p.id === ev.sourceId);
        const to = shown.players.find((p) => p.id === ev.targetId);
        if (!from || !to) continue;
        actors.addArc(
          { x: from.pos.x, y: from.pos.y + 1.55, z: from.pos.z },
          { x: to.pos.x, y: to.pos.y + 1.5, z: to.pos.z },
          ev.element,
        );
      }
      actors.ingest(pendingEvents, shown.players, transport.localPlayerId);
      hud.ingest(pendingEvents, shown);
      sound.ingest(pendingEvents, shown, transport.localPlayerId);
      pendingEvents = [];
    }

    stats.begin('actors');
    actors.alpha = transport.alpha;
    actors.update(shown, transport.localPlayerId, elapsed, dt);
    // After the actors, so the listener is where the camera has just been put.
    sound.update(shown, controller.camera, transport.localPlayerId, dt);
    stats.begin('hud');
    hud.tickIndicators(dt);
    hud.render(shown);
    stats.end();
    // The wand last, over the top, with the depth buffer cleared so it cannot be
    // sliced by anything the player is standing against.
    viewmodel.update(me, input, dt);
    actors.muzzle = viewmodel.muzzleWorld(controller.camera);

    /*
     * The shadow map, on a cadence.
     *
     * Almost everything it contains is nailed down. Three hundred boxes, sixty
     * fixtures and the props do not move for the length of a match, and redrawing
     * them into a depth buffer sixty times a second is the same picture sixty
     * times a second. What *does* move is mages, and only within sixteen metres,
     * and a spell wall, which appears and then stands still.
     *
     * So the pass is skipped on some frames instead. At every other frame a
     * walking mage's shadow trails their feet by a sixtieth of a second, which
     * at five metres a second is eight centimetres of a soft-edged shadow in a
     * dark room — nobody has ever seen it. At one in three it is twelve, which
     * is still nothing, and on a machine that cannot afford the pass it is the
     * difference between a shadow and none.
     *
     * `autoUpdate` is the switch Three.js checks before it renders the pass, so
     * turning it off skips the whole thing rather than doing it and discarding
     * the result.
     */
    shadowTick = (shadowTick + 1) % quality.shadowInterval;
    renderer.shadowMap.autoUpdate = shadowTick === 0;

    /*
     * What the simulation thinks is going on, for the overlay.
     *
     * The tick is the useful half. A still picture with a frozen tick is a dead
     * render loop; a still picture with a tick climbing is the simulation
     * running and a rule holding it — and those two have entirely different
     * causes and looked identical from the outside.
     */
    stats.status = `${shown.match.phase} t=${shown.tick}`
      + (shown.match.phase === 'warmup'
        ? ` warm=${shown.warmup.toFixed(1)} sides=${new Set(shown.players.map((p) => p.faction)).size}`
        : '')
      + (shown.match.phase === 'intermission' ? ` gap=${shown.intermission.toFixed(1)}` : '');

    stats.begin('render');
    /*
     * Counters held across the whole frame, not reset by each pass.
     *
     * `renderer.info` clears itself at the start of every `render` call, and a
     * frame here is several: the composer's scene pass, a bloom pass per mip in
     * each direction, the composite, then the viewmodel. So the overlay was
     * reporting whatever the *last* call did — the wand and a pair of hands,
     * twenty-five draws and eight hundred triangles — which read as an empty
     * arena and sent a real investigation looking for a level that was never
     * missing.
     */
    renderer.info.autoReset = false;
    renderer.info.reset();
    renderer.clear();
    post.render();
    viewmodel.render(renderer);
    stats.sample(renderer, performance.now() - frameStart);
    // Through the wrapper, so a frame that throws next time is caught rather
    // than being the last one that ever runs. See `safeFrame`.
    requestAnimationFrame(safeFrame);
  };

  /**
   * All the one-off work, done behind a screen instead of during the first
   * fight.
   *
   * The order matters. Textures are uploaded before anything is drawn, so the
   * warm frames do not stall on them; the warm frames then build the two sets of
   * programs `compileAsync` cannot reach — the shadow pass, which renders with
   * its own depth materials, and the composer's fullscreen quads, which are
   * bloom's whole cost. Spell effects are warmed separately because none of them
   * exist in the scene until somebody casts, so a scene compile finds nothing.
   */
  const drawFrame = (): void => {
    renderer.clear();
    post.render();
    viewmodel.render(renderer);
  };

  await loading.run([
    {
      label: 'Fetching colours',
      run: () => assets.loadFactionTextures(
        /*
         * All three sides, always.
         *
         * This used to fetch only the sides in the roster at load time, to save
         * video memory. But a side can arrive after loading — a bot seated to
         * balance the teams, somebody joining a persistent server, the next map
         * in a rotation — and a mage whose side was never fetched was drawn in
         * the model's own built-in texture, which belongs to whichever faction
         * the artist exported it in. That is why Order mages turned up in red
         * and Balance mages never in green. Twelve textures is about sixty
         * megabytes of video memory; wrong colours are not a thing to save that
         * by.
         */
        FACTIONS,
      ),
    },
    {
      /*
       * The effects go into the scene *first*.
       *
       * Uploading textures before this walks a scene that contains no spells —
       * so a modelled projectile's textures were never uploaded, and the first
       * Wounding of the match paid for three of them mid-fight. Everything that
       * can appear has to exist before anything measures what exists.
       */
      label: 'Preparing spell effects',
      run: () => {
        /*
         * Warmed in front of the camera rather than under the map.
         *
         * The shadow pass renders from the light and compiles a depth program
         * per material, and its frustum covers the ground the player is standing
         * on — not the void four hundred metres below it. Anything parked down
         * there got its main program and paid for its shadow one later, on being
         * walked past. Three metres ahead is inside both frusta.
         */
        const ahead = new THREE.Vector3(0, 0, -3)
          .applyQuaternion(controller.camera.quaternion)
          .add(controller.camera.position);
        // The roster goes with it: nameplates are sprites, and a sprite is a
        // program nothing else in the scene builds. See `Actors.warmUp`.
        /*
         * Warmed against the roster, not the view.
         *
         * `players` is filtered by relevancy, and at load time that is usually
         * nobody but you — so the nameplates this builds were built for an empty
         * roster, and the `SpriteMaterial` program they exist to compile was
         * never compiled. The bill came due at the start of the match, when the
         * first teammates came into range: a shader compile plus a canvas
         * rasterisation and a texture upload per name, all in one frame, which
         * is a freeze measured in seconds rather than the hitch this was written
         * to remove.
         *
         * The roster is every name the match will ask for, which is exactly what
         * a warm-up wants.
         */
        const here2 = transport.getState();
        actors.warmUp(ahead, here2.roster ?? here2.players, transport.localPlayerId);
      },
    },
    {
      label: 'Uploading textures',
      run: () => {
        uploadTextures(renderer, world.scene);
        uploadTextures(renderer, viewmodel.scene);
      },
    },
    {
      label: 'Compiling shaders',
      run: async () => {
        await renderer.compileAsync(world.scene, controller.camera);
        await renderer.compileAsync(viewmodel.scene, viewmodel.camera);
      },
    },
    {
      label: 'Warming shadows and bloom',
      // Hidden only after the frames are drawn. `compileAsync` builds the base
      // programs; the shadow pass and the composer build their own, and only a
      // real frame with the objects still visible reaches those.
      run: () => {
        warmFrames(drawFrame, 4);
        actors.coolDown();
      },
    },
    {
      /*
       * Decoded here for the same reason the shaders are compiled here.
       * `decodeAudioData` stalls the first time a buffer is used, so a lazily
       * decoded sound costs a frame on the first dart of the match — the same
       * hitch, in a place nobody thinks to look for it.
       *
       * If the player has not clicked yet there is no context to decode into and
       * this does nothing; the sounds then decode on first use, which is worth a
       * frame once and is better than refusing to start.
       */
      label: 'Tuning the choir',
      run: () => sound.prefetch(),
    },
    {
      label: 'Charting the arena',
      run: () => { if (!remote) room.warmUpBots(); },
    },
  ]);

  loading.dismiss();

  /*
   * Exactly one kick.
   *
   * `frame` re-schedules itself, so a second call here does not "make sure" the
   * loop is running — it starts a *second*, independent chain that never merges
   * with the first. Both then run every displayed frame, for the life of the
   * page: two physics steps, two shadow passes, two bloom composites, two HUD
   * rebuilds. It is invisible in the simulation because `clock.getDelta()`
   * splits the elapsed time between them and the second gets nothing, so the
   * match advances at the right rate — it just costs twice as much to draw.
   * Which is why it survived: it does not look like a bug, it looks like a
   * slower computer.
   */
  requestAnimationFrame(safeFrame);
}

/**
 * Puts a frame error where a playtester will see it.
 *
 * Deliberately not a dialog and not fatal: the loop keeps running underneath, so
 * this is a report rather than a tombstone. Fixed to a corner and click-through,
 * because the game is very possibly still playable and covering it would be
 * worse than the fault.
 */
function showFrameError(err: unknown): void {
  const existing = document.getElementById('frame-error');
  if (existing) return;
  const box = document.createElement('div');
  box.id = 'frame-error';
  box.style.cssText = [
    'position:fixed', 'left:12px', 'bottom:12px', 'z-index:10000',
    'max-width:min(680px,90vw)', 'padding:10px 12px',
    'background:rgba(60,10,10,0.92)', 'border:1px solid #d9584a',
    'color:#ffd9d4', 'font:12px/1.5 ui-monospace,Menlo,monospace',
    'white-space:pre-wrap', 'pointer-events:none', 'border-radius:3px',
  ].join(';');
  const message = err instanceof Error ? `${err.message}\n\n${err.stack ?? ''}` : String(err);
  box.textContent = `A frame threw. The game is still running.\n\n${message}`;
  document.body.appendChild(box);
}

void main().catch((err) => {
  console.error(err);
  document.body.innerHTML =
    `<pre style="color:#d9c9a4;font:14px monospace;padding:24px">Failed to start:\n\n${String(err)}</pre>`;
});
